/**
 * Pantalla vacía del chat con IA del admin: saludo, preguntas sugeridas, "Continuar" la última
 * conversación (opcional) y "Repetir una reciente" (la pregunta de una conversación del historial,
 * en una conversación nueva). `extra` va antes de las sugerencias (p. ej. el brief del CEO).
 */
import type { ComponentType, ReactNode } from "react";
import { ArrowRight, Clock, Repeat2, Sparkles } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { cuandoConversacion, type ConversacionIa } from "@/lib/chatIa";

export interface ChatIaBienvenidaProps<T extends ConversacionIa> {
  icono?: ComponentType<{ className?: string }>;
  titulo?: string;
  descripcion: string;
  /** `null` = cargando. */
  sugerencias: readonly string[] | null;
  recientes?: T[];
  /** Conversación para "Continuar donde te quedaste". */
  continuar?: T | null;
  deshabilitado?: boolean;
  onPreguntar: (texto: string) => void;
  onRepetir?: (c: T) => void;
  onContinuar?: (c: T) => void;
  extra?: ReactNode;
}

export function ChatIaBienvenida<T extends ConversacionIa>({
  icono: Icono = Sparkles,
  titulo = "¿En qué te ayudo?",
  descripcion,
  sugerencias,
  recientes = [],
  continuar,
  deshabilitado,
  onPreguntar,
  onRepetir,
  onContinuar,
  extra,
}: ChatIaBienvenidaProps<T>) {
  return (
    <div className="mx-auto flex max-w-lg flex-col items-center pt-6 text-center">
      <span className="mb-3 inline-flex size-12 items-center justify-center rounded-2xl bg-primary/10 text-primary ring-1 ring-primary/15">
        <Icono className="size-6" />
      </span>
      <p className="text-[15px] font-semibold text-foreground">{titulo}</p>
      <p className="mt-1 text-[12.5px] text-muted-foreground">{descripcion}</p>

      {continuar && onContinuar && (
        <button
          type="button"
          onClick={() => onContinuar(continuar)}
          className="mt-5 flex w-full items-center gap-2.5 rounded-xl border border-primary/30 bg-primary/5 px-3 py-2.5 text-left text-[12.5px] transition-colors hover:bg-primary/10"
        >
          <Clock className="size-4 shrink-0 text-primary" />
          <span className="min-w-0 flex-1 truncate text-foreground">
            <span className="font-medium">Continuar:</span> {continuar.titulo}
          </span>
          <span className="shrink-0 text-[11px] text-muted-foreground">{cuandoConversacion(continuar.fecha, new Date())}</span>
        </button>
      )}

      {extra && <div className="mt-5 flex w-full flex-wrap justify-center gap-2">{extra}</div>}

      <div className={cn("grid w-full gap-2 sm:grid-cols-2", extra ? "mt-3" : "mt-5")}>
        {sugerencias === null
          ? [0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-14 rounded-xl" />)
          : sugerencias.map((s) => (
              <button
                key={s}
                type="button"
                disabled={deshabilitado}
                onClick={() => onPreguntar(s)}
                className={cn(
                  "group flex min-h-14 items-center gap-2 rounded-xl border border-border bg-card px-3 py-2.5 text-left text-[12.5px] leading-snug text-foreground",
                  "transition-all hover:border-primary/40 hover:bg-primary/5 hover:shadow-sm",
                  "disabled:cursor-not-allowed disabled:opacity-50",
                )}
              >
                <span className="min-w-0 flex-1 break-words">{s}</span>
                <ArrowRight className="size-3.5 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-primary" />
              </button>
            ))}
      </div>

      {onRepetir && recientes.length > 0 && (
        <div className="mt-6 w-full text-left">
          <p className="mb-1.5 px-1 text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">
            Repetir una reciente
          </p>
          <ul className="divide-y divide-border overflow-hidden rounded-xl border border-border bg-card">
            {recientes.map((c) => (
              <li key={c.id}>
                <button
                  type="button"
                  disabled={deshabilitado}
                  onClick={() => onRepetir(c)}
                  className="group flex w-full items-center gap-2.5 px-3 py-2.5 text-left text-[12.5px] transition-colors hover:bg-muted/60 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Repeat2 className="size-4 shrink-0 text-primary" />
                  <span className="min-w-0 flex-1 truncate text-foreground">{c.titulo}</span>
                  <span className="shrink-0 text-[11px] text-muted-foreground transition-opacity md:opacity-0 md:group-hover:opacity-100">
                    Repetir
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
